import './FirebaseLists2.css';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../authNew/authSlice';
import { BasePageContainer, BaseCard, BaseVerticalContainer, BaseComponentContainer } from '../../../common/viewBaseComponents';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';

export default function FirebaseLists3() {
    const auth = useSelector(selectAuth);
    const [musicData, setMusicData] = useState({}); // State per i dati musicali
    const [filteredArtists, setFilteredArtists] = useState({}); // State per gli artisti filtrati
    const [searchTerm, setSearchTerm] = useState(''); // State per il termine di ricerca
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogOriginalLyrics, setDialogOriginalLyrics] = useState('');
    const [dialogItalianLyrics, setDialogItalianLyrics] = useState('');
    const [dialogHeader, setDialogHeader] = useState('');
    
    // State per la ricerca avanzata
    const [advancedFilters, setAdvancedFilters] = useState({
        artist: '',
        trackName: '',
        albumName: '',
        albumYear: '',
        artistGenre: '',
        albumGenre: '',
        trackPath: '',
        trackFilename: ''
    });

    // Carica i dati dal file JSON
    useEffect(() => {
        fetch('/music/music_db.json') // Cambiato il percorso del file
            .then(response => response.json())
            .then(data => {
                setMusicData(data);
                setFilteredArtists(data); // Inizialmente tutti gli artisti sono mostrati
            })
            .catch(error => {
                console.error('Errore nel caricamento dei dati musicali:', error);
            });
    }, []);

    // Funzione per cercare in tutti i campi del JSON
    const searchInObject = (obj, searchTerm) => {
        const term = searchTerm.toLowerCase();
        for (const key in obj) {
            if (typeof obj[key] === 'string') {
                if (obj[key].toLowerCase().includes(term)) {
                    return true;
                }
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                if (searchInObject(obj[key], searchTerm)) {
                    return true;
                }
            }
        }
        return false;
    };

    // Nuova funzione per filtrare in modo mirato
    const filterArtists = (data, term) => {
        if (term.trim() === '' || term.length < 3) {
            return data;
        }

        const filtered = {};

        for (const artistName in data) {
            const artist = data[artistName];
            const filteredAlbums = {};

            for (const albumName in artist.albums) {
                const album = artist.albums[albumName];
                const filteredTracks = album.tracks.filter(track => searchInObject(track, term));

                if (filteredTracks.length > 0) {
                    // Aggiungi l'album solo se contiene tracce corrispondenti
                    filteredAlbums[albumName] = {
                        ...album,
                        tracks: filteredTracks
                    };
                }
            }

            // Aggiungi l'artista solo se contiene album con tracce corrispondenti
            if (Object.keys(filteredAlbums).length > 0) {
                filtered[artistName] = {
                    ...artist,
                    albums: filteredAlbums
                };
            }
        }

        return filtered;
    };

    // Funzione per filtrare con criteri avanzati
    const filterArtistsAdvanced = (data, filters) => {
        // Controlla se almeno un filtro è compilato
        const hasActiveFilters = Object.values(filters).some(value => value.trim() !== '');
        if (!hasActiveFilters) {
            return data;
        }

        const filtered = {};

        for (const artistName in data) {
            const artist = data[artistName];
            
            // Filtra per artista
            if (filters.artist.trim() !== '' && !artistName.toLowerCase().includes(filters.artist.toLowerCase())) {
                continue;
            }

            const filteredAlbums = {};

            for (const albumName in artist.albums) {
                const album = artist.albums[albumName];
                
                // Filtra per nome album
                if (filters.albumName.trim() !== '' && !albumName.toLowerCase().includes(filters.albumName.toLowerCase())) {
                    continue;
                }

                // Filtra per anno album
                if (filters.albumYear.trim() !== '' && album.info.releaseYear !== filters.albumYear.trim()) {
                    continue;
                }

                // Filtra per genere album
                if (filters.albumGenre.trim() !== '' && (!album.info.genres || !album.info.genres.toLowerCase().includes(filters.albumGenre.toLowerCase()))) {
                    continue;
                }

                // Filtra per genere artista
                if (filters.artistGenre.trim() !== '' && (!artist.info.mainGenre || !artist.info.mainGenre.toLowerCase().includes(filters.artistGenre.toLowerCase()))) {
                    continue;
                }

                const filteredTracks = album.tracks.filter(track => {
                    // Filtra per nome brano
                    if (filters.trackName.trim() !== '' && !track.cleanTitle.toLowerCase().includes(filters.trackName.toLowerCase())) {
                        return false;
                    }

                    // Filtra per path track
                    if (filters.trackPath.trim() !== '' && (!track.path || !track.path.toLowerCase().includes(filters.trackPath.toLowerCase()))) {
                        return false;
                    }

                    // Filtra per filename track
                    if (filters.trackFilename.trim() !== '' && (!track.filename || !track.filename.toLowerCase().includes(filters.trackFilename.toLowerCase()))) {
                        return false;
                    }

                    return true;
                });

                if (filteredTracks.length > 0) {
                    filteredAlbums[albumName] = {
                        ...album,
                        tracks: filteredTracks
                    };
                }
            }

            // Aggiungi l'artista solo se contiene album con tracce corrispondenti
            if (Object.keys(filteredAlbums).length > 0) {
                filtered[artistName] = {
                    ...artist,
                    albums: filteredAlbums
                };
            }
        }

        return filtered;
    };

    // Filtra i dati musicali in base al termine di ricerca o ai filtri avanzati
    useEffect(() => {
        // Controlla se almeno un filtro avanzato è compilato
        const hasAdvancedFilters = Object.values(advancedFilters).some(value => value.trim() !== '');
        
        // Se ci sono filtri avanzati compilati, usa la ricerca avanzata
        if (hasAdvancedFilters) {
            const filtered = filterArtistsAdvanced(musicData, advancedFilters);
            setFilteredArtists(filtered);
        } else if (searchTerm.trim() === '') {
            // Se nessun filtro è attivo, mostra tutti i dati
            setFilteredArtists(musicData);
        } else {
            // Altrimenti usa la ricerca generica
            const filtered = filterArtists(musicData, searchTerm);
            setFilteredArtists(filtered);
        }
    }, [searchTerm, musicData, advancedFilters]);

    const showDialog = (originalLyrics, italianLyrics) => {
        setDialogOriginalLyrics(originalLyrics);
        setDialogItalianLyrics(italianLyrics);
        setDialogHeader('Testi');
        setDialogVisible(true);
    };

    const hideDialog = () => {
        setDialogVisible(false);
        setDialogOriginalLyrics('');
        setDialogItalianLyrics('');
        setDialogHeader('');
    };

    // Helper function to check if a value should be displayed
    const shouldDisplayValue = (value) => {
        return value !== null && value !== undefined && value !== '';
    };

    // Controllo autenticazione
    if (!auth.isLogged) {
        return (
            <BasePageContainer>
                <Card title="Accesso Negato">
                    <p>Devi essere autenticato per visualizzare questa pagina.</p>
                </Card>
            </BasePageContainer>
        );
    }

    return (
        <BasePageContainer className={'w-12 flipleft'}>
            <BaseComponentContainer>
                <BaseVerticalContainer>
                    <Card title="Ricerca Musica" className={'w-12'}>
                        <div className="p-mb-3">
                            <InputText
                                id="search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Cerca per artista, album, traccia, genere, anno, ecc."
                                className="p-d-block search-input-responsive"
                            />
                        </div>

                        {/* Accordion per la ricerca avanzata */}
                        <Accordion className="p-mb-3">
                            <AccordionTab header="Ricerca Avanzata">
                                <div className="p-grid p-fluid">
                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="artistFilter" className="p-d-block p-mb-2"><strong>Artista</strong></label>
                                        <InputText
                                            id="artistFilter"
                                            value={advancedFilters.artist}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, artist: e.target.value})}
                                            placeholder="Nome artista"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="trackNameFilter" className="p-d-block p-mb-2"><strong>Nome Brano</strong></label>
                                        <InputText
                                            id="trackNameFilter"
                                            value={advancedFilters.trackName}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, trackName: e.target.value})}
                                            placeholder="Nome brano"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="albumNameFilter" className="p-d-block p-mb-2"><strong>Nome Album</strong></label>
                                        <InputText
                                            id="albumNameFilter"
                                            value={advancedFilters.albumName}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, albumName: e.target.value})}
                                            placeholder="Nome album"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="albumYearFilter" className="p-d-block p-mb-2"><strong>Anno Album</strong></label>
                                        <InputText
                                            id="albumYearFilter"
                                            value={advancedFilters.albumYear}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, albumYear: e.target.value})}
                                            placeholder="Anno (es. 2023)"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="artistGenreFilter" className="p-d-block p-mb-2"><strong>Genere Artista</strong></label>
                                        <InputText
                                            id="artistGenreFilter"
                                            value={advancedFilters.artistGenre}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, artistGenre: e.target.value})}
                                            placeholder="Genere artista"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="albumGenreFilter" className="p-d-block p-mb-2"><strong>Genere Album</strong></label>
                                        <InputText
                                            id="albumGenreFilter"
                                            value={advancedFilters.albumGenre}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, albumGenre: e.target.value})}
                                            placeholder="Genere album"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="trackPathFilter" className="p-d-block p-mb-2"><strong>Path Track</strong></label>
                                        <InputText
                                            id="trackPathFilter"
                                            value={advancedFilters.trackPath}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, trackPath: e.target.value})}
                                            placeholder="Percorso track"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="trackFilenameFilter" className="p-d-block p-mb-2"><strong>File Name Track</strong></label>
                                        <InputText
                                            id="trackFilenameFilter"
                                            value={advancedFilters.trackFilename}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, trackFilename: e.target.value})}
                                            placeholder="Nome file track"
                                        />
                                    </div>

                                    <div className="p-col-12 p-mb-3">
                                        <Button
                                            label="Resetta Filtri"
                                            icon="pi pi-times"
                                            onClick={() => setAdvancedFilters({
                                                artist: '',
                                                trackName: '',
                                                albumName: '',
                                                albumYear: '',
                                                artistGenre: '',
                                                albumGenre: '',
                                                trackPath: '',
                                                trackFilename: ''
                                            })}
                                            className="p-button-secondary"
                                        />
                                    </div>
                                </div>
                            </AccordionTab>
                        </Accordion>

                        {Object.keys(filteredArtists).length > 0 ? (
                            Object.entries(filteredArtists).map(([artistName, artistData]) => (
                                <BaseCard key={`${artistName}`} className="p-p-3">
                                    <div className="p-d-flex p-flex-column">
                                        <div className="p-mb-2" style={{ fontSize: '2rem' }}>
                                            <strong>{artistName}</strong>
                                        </div>
                                        {shouldDisplayValue(artistData.info.biography) && (
                                            <div className="p-mb-2 p-1">
                                                <strong>Biografia:</strong> {artistData.info.biography}
                                            </div>
                                        )}
                                        {shouldDisplayValue(artistData.info.mainGenre) && (
                                            <div className="p-mb-2 p-1">
                                                <strong>Genere principale:</strong> {artistData.info.mainGenre}
                                            </div>
                                        )}
                                        {shouldDisplayValue(artistData.info.country) && (
                                            <div className="p-mb-2 p-1">
                                                <strong>Nazione:</strong> {artistData.info.country}
                                            </div>
                                        )}
                                        {shouldDisplayValue(artistData.info.activeYears) && (
                                            <div className="p-mb-2 p-1">
                                                <strong>Anni attivi:</strong> {artistData.info.activeYears}
                                            </div>
                                        )}
                                        {shouldDisplayValue(artistData.info.recordLabel) && (
                                            <div className="p-mb-2 p-1">
                                                <strong>Etichetta:</strong> {artistData.info.recordLabel}
                                            </div>
                                        )}

                                        {/* Render albums */}
                                        {Object.entries(artistData.albums).map(([albumName, albumData]) => (
                                            <Accordion key={`${artistName}-${albumName}`} className="p-mt-3">
                                                <AccordionTab header={`${albumData.info.cleanAlbumTitle} (${albumData.info.releaseYear})`}>
                                                    <div className="p-ml-3">
                                                        {shouldDisplayValue(albumData.info.genres) && (
                                                            <div className="p-mb-1">
                                                                <strong>Genere:</strong> {albumData.info.genres}
                                                            </div>
                                                        )}
                                                        {shouldDisplayValue(albumData.info.producers) && (
                                                            <div className="p-mb-1">
                                                                <strong>Produttori:</strong> {albumData.info.producers}
                                                            </div>
                                                        )}
                                                        {shouldDisplayValue(albumData.info.description) && (
                                                            <div className="p-mb-1">
                                                                <strong>Descrizione:</strong> {albumData.info.description}
                                                            </div>
                                                        )}
                                                        {shouldDisplayValue(albumData.info.notableTracks) && (
                                                            <div className="p-mb-1">
                                                                <strong>Brani top:</strong> {albumData.info.notableTracks}
                                                            </div>
                                                        )}

                                                        {/* Render tracks */}
                                                        <div className="p-ml-2">
                                                            <Accordion>
                                                                {albumData.tracks.map((track, trackIndex) => (
                                                                    <AccordionTab
                                                                       key={`${artistName}-${albumName}-${trackIndex}`}
                                                                       header={`${track.trackNumber} - ${track.cleanTitle}`}
                                                                       showHeaderIcon={false}
                                                                       style={albumData.info.notableTracks && albumData.info.notableTracks.toLowerCase().includes(track.cleanTitle.toLowerCase()) ? { backgroundColor: '#c0fcf8ff', margin: '4px', padding: '3px' } : { margin: '4px', padding: '3px' }}
                                                                   >
                                                                        <div className="p-ml-2" style={{ fontSize: '0.9rem' }}>
                                                                            {shouldDisplayValue(track.filename) && (
                                                                                <div><strong>Nome file:</strong> {track.filename}</div>
                                                                            )}
                                                                            {shouldDisplayValue(track.path) && (
                                                                                <div><strong>Percorso:</strong> {track.path}</div>
                                                                            )}
                                                                            {shouldDisplayValue(track.genre) && (
                                                                                <div><strong>Genere:</strong> {track.genre}</div>
                                                                            )}
                                                                            {shouldDisplayValue(track.guestArtists) && (
                                                                                <div><strong>Guest Artists:</strong> {track.guestArtists}</div>
                                                                            )}
                                                                            {shouldDisplayValue(track.description) && (
                                                                                <div><strong>Descrizione:</strong> {track.description}</div>
                                                                            )}
                                                                            {track.originalLyrics && (
                                                                                <div style={ {paddingTop: '7px'}}>
                                                                                    <Button
                                                                                        label="Mostra testi"
                                                                                        icon="pi pi-book"
                                                                                        onClick={() => showDialog(track.originalLyrics, track.italianLyrics)}
                                                                                    />
                                                                                </div>
                                                                            )}


                                                                        </div>
                                                                    </AccordionTab>
                                                                ))}
                                                            </Accordion>
                                                        </div>
                                                    </div>
                                                </AccordionTab>
                                            </Accordion>
                                        ))}
                                    </div>
                                </BaseCard>
                            ))
                        ) : (
                            <Card>
                                <p>Nessun risultato trovato.</p>
                            </Card>
                        )}
                    </Card>
                </BaseVerticalContainer>
            </BaseComponentContainer>
            <Dialog header={dialogHeader} visible={dialogVisible} style={{ width: '80vw' }} onHide={hideDialog}>
                <div className="p-d-flex p-flex-column p-gap-3">
                    <div className="p-d-flex p-flex-row p-flex-wrap p-jc-between p-gap-3">
                        <div className="p-flex-grow-1">
                            <h3>Testo Originale</h3>
                            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{dialogOriginalLyrics}</pre>
                        </div>
                        <div className="p-flex-grow-1">
                            <h3>Testo Italiano</h3>
                            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{dialogItalianLyrics}</pre>
                        </div>
                    </div>
                </div>
            </Dialog>
        </BasePageContainer>
    );
}